import type { NodeSpec, Node as ProsemirrorNode } from 'prosemirror-model';

import type { ListBoxProps } from '@repo/foundations';
import type { MarkdownSerializerState } from '@repo/secoda-editor/lib/markdown/serializer';
import ReactNode from '@repo/secoda-editor/nodes/ReactNode';
import chartBlocksRule from '@repo/secoda-editor/rules/chartblocks';
import type { SecodaEditorComponentProps } from '@repo/secoda-editor/types';
import type { Token } from 'markdown-it';
import type { ParseSpec } from 'prosemirror-markdown';
import { CreateGraphModalWrapper } from '../components/CreateGraphModalWrapper';
import { ChartBlockContainerWrapper } from './ChartBlockContainerWrapper';

export default class ChartBlock extends ReactNode {
	get name() {
		return 'chart_block';
	}

	get markdownToken() {
		return 'chart_block';
	}

	get schema(): NodeSpec {
		return {
			attrs: {
				id: {
					default: '',
				},
				xAxis: {
					default: '',
				},
				yAxis: {
					default: '',
				},
				dimension: {
					default: '',
				},
				numericFormat: {
					default: '',
				},
			},
			group: 'block',
			defining: true,
			atom: true,
			draggable: false,
			parseDOM: [
				{
					preserveWhitespace: 'full',
					tag: 'div.chartblock',
					getAttrs: (dom: HTMLElement | string) =>
						typeof dom === 'string'
							? null
							: {
									id: dom.getAttribute('id'),
									xAxis: dom.getAttribute('data-x-axis'),
									yAxis: dom.getAttribute('data-y-axis'),
									dimension: dom.getAttribute('data-dimension'),
									numericFormat: dom.getAttribute('data-numeric-format'),
								},
				},
			],
			toDOM: (node) => [
				'div',
				{
					...node.attrs,
					class: 'chartblock',
					id: node.attrs.id,
					'data-x-axis': node.attrs.xAxis,
					'data-y-axis': node.attrs.yAxis,
					'data-dimension': node.attrs.dimension,
					'data-numeric-format': node.attrs.numericFormat,
				},
				node.attrs.id,
			],
		};
	}

	toMarkdown(state: MarkdownSerializerState, node: ProsemirrorNode) {
		const { id, xAxis, yAxis, dimension, numericFormat } = node.attrs;
		if (id) {
			state.write(
				`\n\n:;:${id}|${xAxis ?? ''}|${yAxis ?? ''}|${dimension ?? ''}|${numericFormat ?? ''}:;:\n\n`
			);
			state.closeBlock(node);
		}
	}

	parseMarkdown(): ParseSpec {
		return {
			block: 'chart_block',
			getAttrs: (token: Token) => {
				const parsedAttrs = (token.attrs ?? []).reduce(
					(acc, item: [string, string]) => {
						if (item?.length !== 2) {
							return acc;
						}

						return {
							...acc,
							[item[0]]: item[1],
						};
					},
					{} as Record<string, string>
				);

				return {
					id: parsedAttrs.id,
					xAxis: parsedAttrs.xAxis,
					yAxis: parsedAttrs.yAxis,
					dimension: parsedAttrs.dimension,
					numericFormat: parsedAttrs.numericFormat,
				};
			},
		};
	}

	get rulePlugins() {
		return [chartBlocksRule];
	}

	commands() {
		return () => () => true;
	}

	component = (props: SecodaEditorComponentProps) => (
		<ChartBlockContainerWrapper
			{...props}
			readOnly={this.editorState.readOnly}
			onShowToast={this.editor?.options?.onShowToast}
			onTrackEvent={this.editor?.options?.onTrackEvent}
		/>
	);

	preselectionWidget(props: {
		onClose: () => void;
		targetRef: ListBoxProps['targetRef'];
	}) {
		return (
			<CreateGraphModalWrapper onClose={props.onClose} editor={this.editor} />
		);
	}
}
